import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { ContentStatusPill, type ContentStatusPillProps } from './ContentStatusPill';
import { ContentAuthor, type ContentAuthorProps } from './ContentAuthor';
import { ContentDateline, type ContentDatelineProps } from './ContentDateline';

export interface ContentMetaProps {
  content: ContentStatusPillProps['content'] & ContentAuthorProps['content'] & ContentDatelineProps['content'];
  show?: {
    status?: boolean;     // default true
    author?: boolean;     // default true
    date?: boolean;       // default true
    avatar?: boolean;     // default true — avatar pushed to the right
  };
  sx?: SxProps<Theme>;
}

/**
 * The "meta row" — status pill + author name + date + avatar (right-aligned).
 * Composes ContentStatusPill, ContentAuthor, ContentDateline.
 */
export const ContentMeta: React.FC<ContentMetaProps> = ({ content, show = {}, sx }) => {
  const showStatus = show.status !== false;
  const showAuthor = show.author !== false;
  const showDate = show.date !== false;
  const showAvatar = show.avatar !== false;
  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: 12,
          color: (theme) => theme.palette.text.secondary,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {showStatus && <ContentStatusPill content={content} />}
      {(showAuthor || showDate) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px', overflow: 'hidden' }}>
          {showAuthor && (
            <ContentAuthor content={content} show="name" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
          )}
          {showAuthor && showDate && <Box component="span" sx={{ opacity: 0.35 }}>·</Box>}
          {showDate && <ContentDateline content={content} />}
        </Box>
      )}
      {showAvatar && (
        <ContentAuthor content={content} show="avatar" sx={{ marginLeft: 'auto' }} />
      )}
    </Box>
  );
};

export default ContentMeta;
